import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { EhValidators, LoggerService, UserOrg } from '@enliven/angular';
import { catchError, Observable, of, tap } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AmplifyService } from './amplify.service';
import { DataService } from './data.service';
import { E360SessionService } from './e360-session.service';
import { PasswordService } from './password.service';

@Injectable({
  providedIn: 'root'
})
export class DirectLoginService {
  private readonly pwSvc: PasswordService = inject(PasswordService);
  private readonly dataService: DataService = inject(DataService);
  private readonly e360SessionService = inject(E360SessionService);
  private readonly amplifyService = inject(AmplifyService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);

  nonSpacesValidator: ValidatorFn = EhValidators.noSpaces();
  emailValidator: ValidatorFn = EhValidators.email();

  alertMsg: WritableSignal<string> = signal('');
  cognitoUser: WritableSignal<any> = signal(null);
  infoMsg: WritableSignal<string> = signal('');
  submitAttempted: WritableSignal<boolean> = signal(false);

  private _form: FormGroup = new FormGroup({
    username: new FormControl(''),
    email: new FormControl(''),
    code: new FormControl(''),
    pw: new FormControl(''),
    newPw: new FormControl(''),
    pwConf: new FormControl(''),
    tempPw: new FormControl('')
  });

  get form(): FormGroup {
    return this._form;
  }

  get username() {
    return this._form.get('username');
  }

  get usernameError(): boolean {
    return this.username?.invalid && (this.submitAttempted() || (this.username.dirty && this.username.touched));
  }

  get email() {
    return this._form.get('email');
  }

  get emailError(): boolean {
    return this.email?.invalid && (this.submitAttempted() || (this.email.dirty && this.email.touched));
  }

  get code() {
    return this._form.get('code');
  }

  get codeError(): boolean {
    return this.code.invalid && (this.submitAttempted() || (this.code.dirty && this.code.touched));
  }

  get pw() {
    return this._form.get('pw');
  }

  get pwError() {
    return this.pw?.invalid && (this.submitAttempted() || (this.pw.dirty && this.pw.touched));
  }

  get newPw() {
    return this._form.get('newPw');
  }

  get newPwError(): boolean {
    return (
      (this.submitAttempted() || (this.newPw?.dirty && this.newPw.touched)) &&
      (this.newPw.invalid || this._form.errors?.['forbiddenPassword'])
    );
  }

  get pwConf() {
    return this._form.get('pwConf');
  }

  get pwConfError(): boolean {
    return (
      (this.submitAttempted() || (this.pwConf?.dirty && this.pwConf.touched)) &&
      (this.pwConf.invalid || this._form.errors?.['passwordsDoNotMatch'])
    );
  }

  get tempPw() {
    return this._form.get('tempPw');
  }

  get tempPwError(): boolean {
    return this.tempPw.invalid && (this.submitAttempted() || (this.tempPw.dirty && this.tempPw.touched));
  }

  isPwBreached() {
    this.pwSvc.checkForBreach(this.newPw.value);
    if (!this.pwSvc.breached()) return false;
    this.alertMsg.set('PASSWORD_REQS.BREACHED_PASSWORD');
    return true;
  }

  signInForm() {
    this.resetControls();
    this.username.setValidators([EhValidators.required, this.nonSpacesValidator]);
    this.pw.setValidators(EhValidators.required);
    this.updateValuesAndValidity();
  }

  newUserFirstLoginForm() {
    this.resetControls();
    this.email.setValidators([EhValidators.required, this.emailValidator]);
    this.username.setValidators([EhValidators.required, this.nonSpacesValidator]);
    this.updateValuesAndValidity();
  }

  newPwForm() {
    this.infoMsg.set('');
    this.resetControls();
    this.username.setValidators([EhValidators.required, this.nonSpacesValidator]);
    this.newPw.setValidators(EhValidators.required);
    this.pwConf.setValidators(EhValidators.required);
    this.form.setValidators([this.pwSvc.forbiddenPasswordValidator(), this.pwSvc.passwordsDoNotMatchValidator()]);
    this.updateValuesAndValidity();
  }

  forgotPwForm() {
    this.infoMsg.set('');
    this.resetControls();
    this.username.setValidators([EhValidators.required, this.nonSpacesValidator]);
    this.updateValuesAndValidity();
  }

  resetPwForm() {
    this.infoMsg.set('');
    this.resetControls();
    this.code.setValidators([EhValidators.required, this.nonSpacesValidator]);
    this.newPw.setValidators(EhValidators.required);
    this.pwConf.setValidators(EhValidators.required);
    this.form.setValidators([this.pwSvc.forbiddenPasswordValidator(), this.pwSvc.passwordsDoNotMatchValidator()]);
    this.updateValuesAndValidity();
  }

  tempPwForm() {
    this.infoMsg.set('');
    this.resetControls();
    this.tempPw.setValidators([EhValidators.required, this.nonSpacesValidator]);
    this.updateValuesAndValidity();
  }

  // In most cases, primary and fallback org IDs are the same.
  // They differ only for multi-org users
  // Edge case can exist where separate orgs share a Cognito user pool
  // So, org A could log into org B, even though they don't have org B scope.
  // Cognito would authorize this, our APIs forbid it.
  // This fallback handles that by fetching org info for the actual user in this odd situation.
  retrieveOrgData(primaryOrgId: string, fallbackOrgId: string, logger: LoggerService): Observable<UserOrg | null> {
    return this.dataService.getOrgById(primaryOrgId).pipe(
      tap((userOrg: UserOrg) => (this.e360SessionService.orgIdpExternalId = userOrg.idpExternalId ?? '')),
      catchError(primaryError => {
        logger.warn(`Primary org fetch failed. Org ID: ${primaryOrgId}.`, { error: primaryError }, primaryError);
        if (fallbackOrgId !== primaryOrgId) {
          logger.info(`Retrying org fetch with user's org ID: ${fallbackOrgId}`);
          return this.dataService.getOrgById(fallbackOrgId).pipe(
            tap((userOrg: UserOrg) => {
              this.e360SessionService.orgId = fallbackOrgId;
              this.e360SessionService.orgIdpExternalId = userOrg.idpExternalId ?? '';
            }),
            catchError(fallbackError => {
              logger.warn(
                `Secondary org fetch failed. Org ID ${fallbackOrgId}.`,
                { error: fallbackError },
                fallbackError
              );
              return of(null);
            })
          );
        }
        return of(null);
      })
    );
  }

  /**
   * Signs out a direct login user by revoking the refresh token on the server
   * and cleaning up local storage. Always completes sign-out even if token
   * revocation fails (fail-safe behavior).
   * @param message - The error message to display on the error page
   */
  signOut(message: string): void {
    const refreshToken = this.e360SessionService.refreshToken;
    if (refreshToken) {
      this.amplifyService
        .revokeRefreshToken(refreshToken)
        .pipe(
          finalize(() => {
            this.amplifyService.cleanStorageAfterSignOut();
            this.router.navigate(['error'], { state: { message } });
          })
        )
        .subscribe({
          next: () => this.logger.info('Refresh token revoked successfully'),
          error: err => this.logger.warn('Failed to revoke refresh token, proceeding with sign-out anyway', {}, err)
        });
    } else {
      this.amplifyService.cleanStorageAfterSignOut();
      this.router.navigate(['error'], { state: { message } });
    }
  }

  private resetControls() {
    [
      { ctl: this.form, val: undefined },
      { ctl: this.username, val: undefined },
      { ctl: this.email, val: '' },
      { ctl: this.code, val: '' },
      { ctl: this.pw, val: '' },
      { ctl: this.newPw, val: '' },
      { ctl: this.pwConf, val: '' },
      { ctl: this.tempPw, val: '' }
    ].forEach(({ ctl, val }) => {
      ctl.clearValidators();
      ctl.markAsPristine();
      ctl.markAsUntouched();
      if (val === undefined) return;
      ctl.setValue(val);
    });
  }

  private updateValuesAndValidity() {
    this.form.updateValueAndValidity();
    this.username.updateValueAndValidity();
    this.email.updateValueAndValidity();
    this.code.updateValueAndValidity();
    this.pw.updateValueAndValidity();
    this.newPw.updateValueAndValidity();
    this.pwConf.updateValueAndValidity();
    this.tempPw.updateValueAndValidity();
  }
}
